import { Listbox } from '@/components/ui/listbox';
import { MoneyInput } from '@/components/ui/money-input';
import type { PopoverPortalTarget } from '@/components/ui/popover-portal';
import { cn } from '@/utils/cn';

/**
 * Exact amount or a min/max range — same consolidated shape as DateRangeFilter.
 * From/to money fields only appear when the user chooses a range.
 */

export type AmountFilterMode = 'any' | 'exact' | 'range';

export interface AmountFilter {
  mode: AmountFilterMode;
  /** Exact amount when mode is `exact`. */
  amountMinor: number | null;
  fromMinor: number | null;
  toMinor: number | null;
}

export interface AmountRangeFilterProps {
  value: AmountFilter;
  onChange: (value: AmountFilter) => void;
  currency: string;
  labels: {
    any: string;
    exact: string;
    range: string;
    amount: string;
    from: string;
    to: string;
  };
  disabled?: boolean;
  popoverPortal?: PopoverPortalTarget;
  className?: string;
}

export const emptyAmountFilter = (): AmountFilter => ({
  mode: 'any',
  amountMinor: null,
  fromMinor: null,
  toMinor: null,
});

/** Bounds to send to the API, or null when the filter is idle. */
export const amountFilterBounds = (
  value: AmountFilter,
): { amountMinMinor?: number; amountMaxMinor?: number } => {
  if (value.mode === 'exact' && value.amountMinor != null) {
    return { amountMinMinor: value.amountMinor, amountMaxMinor: value.amountMinor };
  }
  if (value.mode === 'range') {
    return {
      ...(value.fromMinor != null ? { amountMinMinor: value.fromMinor } : {}),
      ...(value.toMinor != null ? { amountMaxMinor: value.toMinor } : {}),
    };
  }
  return {};
};

export const isAmountFilterApplied = (value: AmountFilter): boolean => {
  if (value.mode === 'exact') return value.amountMinor != null;
  if (value.mode === 'range') return value.fromMinor != null || value.toMinor != null;
  return false;
};

export const AmountRangeFilter = ({
  value,
  onChange,
  currency,
  labels,
  disabled = false,
  popoverPortal = 'body',
  className,
}: AmountRangeFilterProps) => {
  const options = [
    { value: 'any' as const, label: labels.any },
    { value: 'exact' as const, label: labels.exact },
    { value: 'range' as const, label: labels.range },
  ];

  return (
    <div className={cn('flex flex-wrap items-end gap-2', className)}>
      <Listbox<AmountFilterMode>
        value={value.mode}
        onChange={(mode) => {
          if (!mode) return;
          onChange(
            mode === 'any'
              ? emptyAmountFilter()
              : mode === 'exact'
                ? { mode, amountMinor: value.amountMinor, fromMinor: null, toMinor: null }
                : {
                    mode,
                    amountMinor: null,
                    fromMinor: value.fromMinor ?? value.amountMinor,
                    toMinor: value.toMinor,
                  },
          );
        }}
        options={options}
        disabled={disabled}
        className="w-40"
        popoverPortal={popoverPortal}
      />

      {value.mode === 'exact' ? (
        <div className="w-40">
          <MoneyInput
            value={value.amountMinor}
            onChange={(amountMinor) => onChange({ ...value, amountMinor })}
            currency={currency}
            disabled={disabled}
            popoverPortal={popoverPortal}
            aria-label={labels.amount}
            size="md"
          />
        </div>
      ) : null}

      {value.mode === 'range' ? (
        <>
          <div className="w-36">
            <MoneyInput
              value={value.fromMinor}
              onChange={(fromMinor) => onChange({ ...value, fromMinor })}
              currency={currency}
              disabled={disabled}
              max={value.toMinor ?? undefined}
              popoverPortal={popoverPortal}
              aria-label={labels.from}
            />
          </div>
          <div className="w-36">
            <MoneyInput
              value={value.toMinor}
              onChange={(toMinor) => onChange({ ...value, toMinor })}
              currency={currency}
              disabled={disabled}
              min={value.fromMinor ?? undefined}
              popoverPortal={popoverPortal}
              aria-label={labels.to}
            />
          </div>
        </>
      ) : null}
    </div>
  );
};
